"use client";

import { useSettings } from "@/context/SettingsContext";
import { useEffect, useRef } from "react";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { useRouter } from "next/navigation";

const decodeHTMLEntities = (text: string) => {
  if (!text) return "";
  return text.replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\u00A0/g, ' ');
};

export default function PrivacyPolicy() {
  const settings = useSettings();
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    let ctx: any;
    import("gsap").then(({ default: gsap }) => {
      ctx = gsap.context(() => {
        gsap.fromTo(".fade-in-up",
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: "power3.out" }
        );
      }, containerRef);
    });

    return () => { if (ctx) ctx.revert(); };
  }, [settings.privacyPolicy]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden pt-36 md:pt-48 pb-24 relative" ref={containerRef}>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-[#e0f2fe]/50 to-transparent pointer-events-none z-0"></div>
      <div className="absolute top-[20%] -left-[10%] w-[500px] h-[500px] bg-[#0d9488]/5 rounded-full blur-3xl pointer-events-none z-0"></div>
      <div className="absolute top-[40%] -right-[10%] w-[400px] h-[400px] bg-teal-100/40 rounded-full blur-3xl pointer-events-none z-0"></div>

      <div className="max-w-4xl mx-auto w-full px-6 relative z-10">

        {/* Minimal Professional Back Button */}
        <div className="fade-in-up mb-10 w-full flex justify-start">
          <button
            onClick={() => router.back()}
            className="group flex items-center gap-3 text-[11px] md:text-[13px] font-bold uppercase tracking-[0.15em] text-gray-400 hover:text-[#0d9488] transition-colors duration-300"
          >
            <div className="w-8 h-8 rounded-full border border-gray-200 group-hover:border-[#0d9488] flex items-center justify-center transition-colors duration-300">
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform duration-300" />
            </div>
            Back
          </button>
        </div>

        {/* Header Section */}
        <div className="fade-in-up mb-12 text-center flex flex-col items-center">
          <div className="w-16 h-16 bg-teal-50 border border-teal-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
            <ShieldCheck className="w-8 h-8 text-[#0d9488]" />
          </div>
          <span className="text-[#0d9488] font-bold text-[11px] tracking-widest uppercase mb-3 block">
            Regulatory Compliance
          </span>
          <h1 className="text-[40px] md:text-[56px] font-serif font-extrabold tracking-tight text-gray-900 leading-tight mb-4">
            Privacy Policy
          </h1>
          {/* <p className="text-[14px] text-gray-500 font-medium">
            Effective Date: June 27, 2026. Compare All Consultancy Services Ltd.
          </p> */}
        </div>

        {/* Content Container */}
        <div className="fade-in-up bg-white rounded-3xl p-8 md:p-14 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100">

          {settings.privacyPolicy && settings.privacyPolicy.trim() !== '<p><br></p>' && settings.privacyPolicy.trim() !== '' ? (
            <div
              className="prose prose-lg max-w-full w-full break-words [&_*]:!whitespace-normal
                         prose-headings:font-serif prose-headings:font-bold prose-headings:text-gray-900 
                         prose-h2:text-[24px] prose-h2:mt-10 prose-h2:mb-4
                         prose-h3:text-[20px] prose-h3:mt-8 prose-h3:mb-3
                         prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-[15px]
                         prose-a:text-[#0d9488] prose-a:font-semibold hover:prose-a:text-teal-700 prose-a:transition-colors
                         prose-li:text-gray-600 prose-li:text-[15px]
                         prose-strong:text-gray-900"
              dangerouslySetInnerHTML={{ __html: decodeHTMLEntities(settings.privacyPolicy) }}
            />
          ) : (
            <div className="prose prose-lg max-w-full w-full break-words font-sans text-[15px] text-gray-600 leading-relaxed space-y-8">
              <p className="font-medium text-gray-900 text-[16px] leading-relaxed">
                Compare All Consultancy Services Ltd (“we”, “us”, or “our”) is committed to protecting the confidentiality and security of your corporate and personal data. This privacy policy explains how we collect, process, and protect information when you visit our website, complete diagnostic questionnaires, or engage our advisory services.
              </p>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  1. Information We Collect
                </h2>
                <p>
                  We collect information that you submit directly to us via our contact forms, quote configurators, or through email communications. This may include:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Contact details: Name, email address, phone number.</li>
                  <li>Corporate details: Company name, job title, industry sector.</li>
                  <li>Sourcing information: Category spends, active vendor names, renewal schedules, and contract parameters.</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  2. How We Use Your Information
                </h2>
                <p>
                  We process your information strictly for the following purposes:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>To evaluate cost benchmark potentials and deliver requested fee options.</li>
                  <li>To provide senior advisory services and manage ongoing client engagements.</li>
                  <li>To communicate administrative updates, policy changes, or respond to direct office inquiries.</li>
                  <li>To deliver our quarterly procurement digests (only when you explicitly opt-in).</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  3. Data Security and Confidentiality
                </h2>
                <p>
                  As independent commercial advisors, confidentiality is foundational to our operations. We implement rigid technical and organizational safeguards to protect your category expenditure files and contract documents. All files shared with us for benchmarking are stored in encrypted environments and are only accessible by authorized category analysts. We never sell, trade, or share your data with suppliers, software publishers, or third-party brokers.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  4. Your Regulatory Rights
                </h2>
                <p>
                  Depending on your location (such as the UK or EU under GDPR), you hold statutory rights regarding your personal data. This includes the right to request access to the data we hold, correct errors, object to processing, or request full erasure of your historical records. To exercise these rights, please contact our data officer at <a href="mailto:info@compareall.com" className="text-[#0d9488] font-semibold hover:underline">info@compareall.com</a>.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  5. Updates to This Policy
                </h2>
                <p>
                  We may revise this privacy policy from time to time to reflect operational modifications or regulatory updates. We recommend checking this page periodically to remain informed about our data handling workflows.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
